import { useEffect, useState } from "react";
import { supabase } from "../supabaseClient";

const GOLD         = "#C9A96E";
const GOLD_PALE    = "#F0DEB4";
const GOLD_BG      = "#FEF9EE";
const INK          = "#1C1915";
const INK_SOFT     = "#4A4540";
const INK_MUTED    = "#9A9390";
const BORDER       = "#E7E1D8";
const SOFT_BG      = "#F7F5F2";
const WHITE        = "#FFFFFF";
const RED          = "#C0392B";
const RED_BG       = "#FDEDEC";
const RED_BORDER   = "#F2C4C0";
const BLUE         = "#2E6DB4";
const BLUE_BG      = "#EEF4FD";
const BLUE_BORDER  = "#C5D9F2";
const GREEN        = "#1A7A44";
const GREEN_BG     = "#E8F6EF";
const GREEN_BORDER = "#B2DEC8";
const ORANGE       = "#E87A2A";
const ORANGE_BG    = "#FEF0E6";
const ORANGE_BORDER= "#F5C9A0";
const PURPLE       = "#7C6FAE";
const PURPLE_BG    = "#F0EEF9";
const PURPLE_BORDER= "#D0C8EC";

const STATUTS = {
  en_attente:         { label: "Lien envoyé",      color: BLUE,   bg: BLUE_BG,   border: BLUE_BORDER },
  questionnaire_recu: { label: "Reçu — à traiter", color: ORANGE, bg: ORANGE_BG, border: ORANGE_BORDER },
  qualifie:           { label: "Qualifié",          color: GREEN,  bg: GREEN_BG,  border: GREEN_BORDER },
  non_qualifie:       { label: "Non qualifié",      color: RED,    bg: RED_BG,    border: RED_BORDER },
  converti:           { label: "Converti ✓",        color: PURPLE, bg: PURPLE_BG, border: PURPLE_BORDER },
};

function getStatut(s) {
  return STATUTS[s] || { label: s || "—", color: INK_MUTED, bg: SOFT_BG, border: BORDER };
}

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

function Badge({ status }) {
  const s = getStatut(status);
  if (cabinetId == null) {
    return <div style={{ padding: 24, textAlign: "center", color: "#8B7361" }}>Chargement...</div>;
  }

  return (
    <span style={{ fontSize: 10, fontWeight: 700, color: s.color, background: s.bg,
      border: `1px solid ${s.border}`, borderRadius: 999, padding: "3px 10px", whiteSpace: "nowrap" }}>
      {s.label}
    </span>
  );
}

function Row({ label, value }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, marginBottom: 10 }}><span style={{ fontSize: 10, letterSpacing: "0.1em", textTransform: "uppercase",
        color: INK_MUTED, fontWeight: 700 }}>{label}</span><span style={{ fontSize: 13, color: value ? INK : INK_MUTED, fontStyle: value ? "normal" : "italic" }}>
        {value || "—"}
      </span></div>
  );
}

export default function PreConsultationsPage({ setActiveTab, cabinetId = null }) {
  const [dossiers,   setDossiers]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [selected,   setSelected]   = useState(null);
  const [converting, setConverting] = useState(false);
  const [converted,  setConverted]  = useState(null);
  const [search,     setSearch]     = useState("");
  const [filterStatut, setFilterStatut] = useState("tous");

  useEffect(() => { if (cabinetId == null) return; charger(); }, [cabinetId]);

  async function charger() {
    setLoading(true);
    const { data } = await supabase
      .from("pre_consultations")
      .select("*")
      .eq("cabinet_id", cabinetId)
      .order("created_at", { ascending: false });
    if (data) setDossiers(data);
    setLoading(false);
  }

  async function changerStatut(id, status) {
    await supabase.from("pre_consultations").update({ status }).eq("id", id);
    setSelected(prev => prev ? { ...prev, status } : prev);
    charger();
  }

  async function convertirEnProjet(dossier) {
    setConverting(true);
    const d = dossier.data || {};

    // Construire le nom du projet
    const nomClient = d.contactNom || dossier.client_name || "Client";
    const typeProjet = Array.isArray(d.typeProjetTypes) && d.typeProjetTypes.length > 0
      ? d.typeProjetTypes[0]
      : "Projet";
    const nomProjet = `${typeProjet} — ${nomClient}`;

    // Mapper le budget
    const budgetBrut = d.budget || "";
    let budgetNum = null;
    if (budgetBrut.includes("200 000–500 000")) budgetNum = 350000;
    else if (budgetBrut.includes("500 000–1 000 000")) budgetNum = 750000;
    else if (budgetBrut.includes("> 1 000 000")) budgetNum = 1200000;
    else if (budgetBrut.includes("< 200 000")) budgetNum = 150000;

    // Créer le projet
    const { data: projet, error } = await supabase
      .from("projects")
      .insert([{
        cabinet_id: cabinetId,
        name: nomProjet,
        client_name: nomClient,
        status: "diagnostic",
        budget: budgetNum,
        progress: 0,
        description: [
          d.adresse ? `Adresse : ${d.adresse}` : null,
          d.delai ? `Délai : ${d.delai}` : null,
          Array.isArray(d.accompagnement) && d.accompagnement.length > 0
            ? `Accompagnement : ${d.accompagnement.join(", ")}` : null,
          Array.isArray(d.styles) && d.styles.length > 0
            ? `Styles : ${d.styles.join(", ")}` : null,
          d.detailsProjet ? `Notes : ${d.detailsProjet}` : null,
        ].filter(Boolean).join("\n"),
      }])
      .select()
      .eq("cabinet_id", cabinetId)
      .single();

    if (error) {
      alert("Erreur lors de la création du projet : " + error.message);
      setConverting(false);
      return;
    }

    // Mettre à jour le statut du dossier
    await supabase
      .from("pre_consultations")
      .update({ status: "converti", projet_id: projet.id })
      .eq("id", dossier.id);

    setConverting(false);
    setSelected(prev => prev ? { ...prev, status: "converti" } : prev);
    charger();
    if (setActiveTab) setActiveTab("projets:" + projet.id);
  }

  const dossiersFiltres = dossiers.filter(d => {
    const matchStatut = filterStatut === "tous" || d.status === filterStatut;
    const nom = (d.client_name || d.data?.contactNom || d.client_email || "").toLowerCase();
    const matchSearch = !search || nom.includes(search.toLowerCase());
    return matchStatut && matchSearch;
  });

  const dossiersTries = [...dossiersFiltres].sort((a, b) => {
    const ap = a.status === "questionnaire_recu" ? 0 : 1;
    const bp = b.status === "questionnaire_recu" ? 0 : 1;
    if (ap !== bp) return ap - bp; // "Reçu — à traiter" en haut
    const ad = new Date(a.created_at || 0).getTime();
    const bd = new Date(b.created_at || 0).getTime();
    return bd - ad; // puis plus récent d'abord
  });

  // ── VUE DÉTAIL ─────────────────────────────────────────────────────────────
  if (selected) {
    const d = selected.data || {};
    const statut = getStatut(selected.status);
    const piecesSelectionnees = (d.pieces || []).filter(p => p.inclure);
    const exterieursSelectionnes = (d.exterieurs || []).filter(p => p.inclure);

    return (
      <div style={{ fontFamily: "system-ui, sans-serif", color: INK, maxWidth: 1100, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}><button onClick={() => { setSelected(null); setConverted(null); }}
            style={{ background: "none", border: `1px solid ${BORDER}`, borderRadius: 7,
              padding: "6px 14px", cursor: "pointer", fontSize: 12, color: INK_SOFT }}>
            ← Retour
          </button><div style={{ flex: 1 }}><div style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: GOLD, fontWeight: 700 }}>
              Dossier pré-consultation
            </div><div style={{ fontSize: 20, fontWeight: 800, color: INK }}>
              {d.contactNom || selected.client_name || selected.client_email || "Sans nom"}
            </div></div><Badge status={selected.status} /></div>

        {/* Confirmation conversion */}
        {converted && (
          <div style={{ background: GREEN_BG, border: `1px solid ${GREEN_BORDER}`, borderRadius: 12,
            padding: "16px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}><span style={{ fontSize: 20 }}>✓</span><div style={{ flex: 1 }}><div style={{ fontSize: 13, fontWeight: 700, color: GREEN }}>
                Projet créé avec succès : {converted.name}
              </div><div style={{ fontSize: 12, color: INK_SOFT }}>
                Le dossier a été converti en projet. Statut : Diagnostic.
              </div></div><button onClick={() => setActiveTab && setActiveTab("projets")}
              style={{ padding: "8px 18px", background: GREEN, border: "none", borderRadius: 8,
                fontSize: 12, fontWeight: 700, color: WHITE, cursor: "pointer" }}>
              Voir le projet →
            </button></div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>

          {/* Colonne gauche */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

            {/* Coordonnées */}
            <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "20px 22px" }}><div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
                color: GOLD, fontWeight: 700, marginBottom: 14 }}>Coordonnées</div><Row label="Nom" value={d.contactNom || selected.client_name} /><Row label="Email" value={d.contactEmail || selected.client_email} /><Row label="Téléphone" value={d.contactTel} /><Row label="Adresse du projet" value={d.adresse} /><Row label="Reçu le" value={fmtDate(selected.created_at)} /></div>

            {/* Projet */}
            <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "20px 22px" }}><div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
                color: GOLD, fontWeight: 700, marginBottom: 14 }}>Projet</div><Row label="Type de projet" value={Array.isArray(d.typeProjetTypes) ? d.typeProjetTypes.join(", ") : d.typeProjetTypes} /><Row label="Budget" value={d.budget} /><Row label="Délai" value={d.delai} /><Row label="Accompagnement" value={Array.isArray(d.accompagnement) ? d.accompagnement.join(", ") : d.accompagnement} />
              {d.detailsProjet && <Row label="Détails" value={d.detailsProjet} />}
            </div>

            {/* Style */}
            {Array.isArray(d.styles) && d.styles.length > 0 && (
              <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "20px 22px" }}><div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
                  color: GOLD, fontWeight: 700, marginBottom: 12 }}>Styles souhaités</div><div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {d.styles.map(s => (
                    <span key={s} style={{ fontSize: 11, fontWeight: 600, color: GOLD,
                      background: GOLD_BG, border: `1px solid ${GOLD_PALE}`,
                      borderRadius: 999, padding: "3px 10px" }}>
                      {s}
                    </span>
                  ))}
                </div></div>
            )}
          </div>

          {/* Colonne droite */}
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

            {/* Changer statut */}
            <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "20px 22px" }}><div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
                color: GOLD, fontWeight: 700, marginBottom: 12 }}>Changer le statut</div><div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {Object.entries(STATUTS).filter(([k]) => k !== "converti").map(([key, s]) => (
                  <button key={key}
                    onClick={() => changerStatut(selected.id, key)}
                    style={{ padding: "7px 14px", borderRadius: 8, fontSize: 11, fontWeight: 600,
                      cursor: "pointer", border: `1px solid ${selected.status === key ? s.color : BORDER}`,
                      background: selected.status === key ? s.bg : WHITE,
                      color: selected.status === key ? s.color : INK_SOFT }}>
                    {s.label}
                  </button>
                ))}
              </div></div>

            {/* Pièces sélectionnées */}
            {piecesSelectionnees.length > 0 && (
              <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "20px 22px" }}><div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
                  color: GOLD, fontWeight: 700, marginBottom: 12 }}>
                  Pièces intérieures ({piecesSelectionnees.length})
                </div><div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {piecesSelectionnees.map((p, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between",
                      fontSize: 12, padding: "5px 0", borderBottom: `1px solid ${BORDER}` }}><span style={{ color: INK, fontWeight: 600 }}>{p.nom}</span><span style={{ color: INK_MUTED }}>
                        {p.nombre ? `×${p.nombre}` : ""} {p.type || ""}
                      </span></div>
                  ))}
                </div></div>
            )}

            {/* Extérieurs */}
            {exterieursSelectionnes.length > 0 && (
              <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "20px 22px" }}><div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
                  color: GOLD, fontWeight: 700, marginBottom: 12 }}>
                  Espaces extérieurs ({exterieursSelectionnes.length})
                </div><div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {exterieursSelectionnes.map((p, i) => (
                    <div key={i} style={{ display: "flex", justifyContent: "space-between",
                      fontSize: 12, padding: "5px 0", borderBottom: `1px solid ${BORDER}` }}><span style={{ color: INK, fontWeight: 600 }}>{p.nom}</span><span style={{ color: INK_MUTED }}>{p.type || ""}</span></div>
                  ))}
                </div></div>
            )}

            {/* Objectifs */}
            {Array.isArray(d.objectifs) && d.objectifs.length > 0 && (
              <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 12, padding: "20px 22px" }}><div style={{ fontSize: 10, letterSpacing: "0.14em", textTransform: "uppercase",
                  color: GOLD, fontWeight: 700, marginBottom: 12 }}>Objectifs</div><div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {d.objectifs.map(o => (
                    <span key={o} style={{ fontSize: 11, color: BLUE, background: BLUE_BG,
                      border: `1px solid ${BLUE_BORDER}`, borderRadius: 999, padding: "3px 10px" }}>
                      {o}
                    </span>
                  ))}
                </div></div>
            )}
          </div></div>

        {/* BOUTON CONVERTIR */}
        {selected.status !== "converti" && (
          <div style={{ marginTop: 20, background: GOLD_BG, border: `1px solid ${GOLD_PALE}`,
            borderRadius: 14, padding: "24px 28px", display: "flex",
            justifyContent: "space-between", alignItems: "center" }}><div><div style={{ fontSize: 14, fontWeight: 800, color: INK, marginBottom: 4 }}>
                 Convertir en projet YOMA
              </div><div style={{ fontSize: 12, color: INK_SOFT }}>
                Crée automatiquement un projet pré-rempli avec les données de ce dossier.
                Statut initial : <strong>Diagnostic</strong>.
              </div></div><button
              onClick={() => convertirEnProjet(selected)}
              disabled={converting}
              style={{ padding: "12px 28px", background: converting ? "#DDD" : GOLD,
                border: "none", borderRadius: 10, fontSize: 13, fontWeight: 700,
                color: WHITE, cursor: converting ? "not-allowed" : "pointer",
                whiteSpace: "nowrap" }}>
              {converting ? "Création en cours…" : "Créer le projet"}
            </button></div>
        )}
      </div>
    );
  }

  // ── VUE LISTE ──────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: "system-ui, sans-serif", color: INK, maxWidth: 1100, margin: "0 auto" }}>

      {/* En-tête */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 24 }}><div><div style={{ fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase",
            color: GOLD, fontWeight: 700, marginBottom: 4 }}>
            YOMA DESIGN — Entrée
          </div><div style={{ fontSize: 22, fontWeight: 800, color: INK }}>Pré-consultations</div><div style={{ fontSize: 12, color: INK_MUTED, marginTop: 4 }}>
            {dossiers.length} dossier{dossiers.length !== 1 ? "s" : ""} reçu{dossiers.length !== 1 ? "s" : ""}
          </div></div><button onClick={() => setActiveTab && setActiveTab("formulaire-prospect")}
          style={{ padding: "10px 20px", background: GOLD, border: "none", borderRadius: 9,
            fontSize: 12, fontWeight: 700, color: WHITE, cursor: "pointer" }}>
          ＋ Envoyer un lien
        </button></div>

      {/* Compteurs statut */}
      <div style={{ display: "flex", gap: 8, marginBottom: 20, flexWrap: "wrap" }}><div onClick={() => setFilterStatut("tous")}
          style={{ padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600,
            background: filterStatut === "tous" ? INK : WHITE,
            color: filterStatut === "tous" ? WHITE : INK_SOFT,
            border: `1px solid ${filterStatut === "tous" ? INK : BORDER}` }}>
          Tous ({dossiers.length})
        </div>
        {Object.entries(STATUTS).map(([key, s]) => {
          const n = dossiers.filter(d => d.status === key).length;
          if (n === 0) return null;
          const active = filterStatut === key;
          return (
            <div key={key} onClick={() => setFilterStatut(active ? "tous" : key)}
              style={{ padding: "8px 16px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600,
                background: active ? s.bg : WHITE,
                color: active ? s.color : INK_SOFT,
                border: `1px solid ${active ? s.color : BORDER}` }}>
              {s.label} ({n})
            </div>
          );
        })}
      </div>

      {/* Recherche */}
      <input value={search} onChange={e => setSearch(e.target.value)}
        placeholder="Rechercher un dossier…"
        style={{ width: "100%", maxWidth: 380, padding: "10px 14px", borderRadius: 8,
          border: `1px solid ${BORDER}`, fontSize: 13, color: INK,
          background: WHITE, outline: "none", boxSizing: "border-box", marginBottom: 16 }} />

      {/* Liste */}
      {loading ? (
        <div style={{ padding: 40, textAlign: "center", color: INK_MUTED, fontStyle: "italic" }}>
          Chargement…
        </div>
      ) : dossiersFiltres.length === 0 ? (
        <div style={{ background: WHITE, border: `1px solid ${BORDER}`, borderRadius: 14,
          padding: "48px 24px", textAlign: "center" }}><div style={{ fontSize: 32, marginBottom: 12 }}>—</div><div style={{ fontSize: 14, fontWeight: 600, color: INK, marginBottom: 6 }}>
            Aucun dossier reçu
          </div><div style={{ fontSize: 12, color: INK_MUTED, marginBottom: 20 }}>
            Envoyez un lien de pré-consultation à votre prochain client
          </div><button onClick={() => setActiveTab && setActiveTab("formulaire-prospect")}
            style={{ padding: "10px 24px", background: GOLD, border: "none", borderRadius: 8,
              fontSize: 12, fontWeight: 700, color: WHITE, cursor: "pointer" }}>
            ＋ Envoyer un lien
          </button></div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {dossiersTries.map(dossier => {
            const d = dossier.data || {};
            const nom = d.contactNom || dossier.client_name || dossier.client_email || "Sans nom";
            const isNew = dossier.status === "questionnaire_recu";
            const email = d.contactEmail || dossier.client_email || "—";
            const budget = d.budget || null;
            const typeProjet = Array.isArray(d.typeProjetTypes) && d.typeProjetTypes.length > 0 ? d.typeProjetTypes[0] : null;
            return (
              <div key={dossier.id}
                onClick={() => { setSelected(dossier); setConverted(null); }}
                style={{ background: isNew ? ORANGE_BG : WHITE,
                  border: `1px solid ${isNew ? ORANGE_BORDER : BORDER}`,
                  borderRadius: 12, padding: "16px 20px", cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 16 }}>

                {/* Point orange si nouveau */}
                {isNew && <div style={{ width: 8, height: 8, borderRadius: "50%",
                  background: ORANGE, flexShrink: 0 }} />}

                {/* Avatar */}
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: GOLD,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontSize: 15, fontWeight: 800, color: WHITE, flexShrink: 0 }}>
                  {(nom[0] || "?").toUpperCase()}
                </div>

                {/* Infos */}
                <div style={{ flex: 1 }}><div style={{ fontSize: 14, fontWeight: 700, color: INK, marginBottom: 3 }}>{nom}</div><div style={{ fontSize: 12, color: INK_SOFT, display: "flex", flexWrap: "wrap", gap: 12, rowGap: 6 }}><span>{email}</span>
                    {budget && <span> {budget}</span>}
                    {typeProjet && <span> {typeProjet}</span>}
                  </div></div>

                {/* Date + statut */}
                <div style={{ textAlign: "right", flexShrink: 0 }}><div style={{ marginBottom: 6 }}><Badge status={dossier.status} /></div><div style={{ fontSize: 11, color: INK_MUTED }}>{fmtDate(dossier.created_at)}</div></div><button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelected(dossier);
                    setConverted(null);
                  }}
                  style={{
                    padding: "8px 12px",
                    background: WHITE,
                    border: `1px solid ${BORDER}`,
                    borderRadius: 10,
                    fontSize: 12,
                    fontWeight: 700,
                    color: INK_SOFT,
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                  }}
                >
                  Ouvrir
                </button><span style={{ color: BORDER, fontSize: 18 }}>›</span></div>
            );
          })}
        </div>
      )}
    </div>
  );
}
